import { Link } from "@inertiajs/react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"

import { RaceShield } from "@/components/race-shield"
import { RaceTag } from "@/components/race-tag"
import { TeamName } from "@/components/team-name"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { getTeamInitials } from "@/lib/race-colors"
import { cn } from "@/lib/utils"
import { leagueMatchPath } from "@/routes"
import type { ScheduleMatch, ScheduleRound } from "@/types"

function RoundStatusBadge({
  status,
  scheduledAt,
}: {
  status: ScheduleRound["status"]
  scheduledAt?: string | null
}) {
  if (status === "completed") {
    return (
      <span className="font-display inline-flex items-center gap-1 border border-[oklch(0.35_0.08_145)] bg-[oklch(0.22_0.04_145/0.3)] px-2 py-0.5 text-xs font-semibold tracking-widest text-[oklch(0.7_0.14_145)]">
        <svg
          className="size-3"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20 6 9 17l-5-5" />
        </svg>
        <span>Complete</span>
      </span>
    )
  }
  if (status === "in_progress") {
    return (
      <span className="font-display text-accent inline-flex items-center gap-1.5 border border-[oklch(0.45_0.1_85)] bg-[oklch(0.25_0.06_85/0.3)] px-2 py-0.5 text-xs font-semibold tracking-widest">
        <span className="bg-accent animate-pulse-dot inline-block h-1.5 w-1.5 rounded-full" />
        <span>Match Day</span>
      </span>
    )
  }
  const label = scheduledAt
    ? new Date(scheduledAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })
    : "Upcoming"
  return (
    <span className="border-border font-display text-muted-foreground border bg-[oklch(0.2_0.01_50/0.5)] px-2 py-0.5 text-xs font-semibold tracking-widest">
      {label}
    </span>
  )
}

function MatchRow({
  match,
  userTeamId,
  leagueId,
}: {
  match: ScheduleMatch
  userTeamId: string | null
  leagueId: string
}) {
  const isUserMatch =
    userTeamId !== null &&
    (match.home_team.id === userTeamId || match.away_team.id === userTeamId)
  const isCompleted = match.status === "completed"
  const hasScores = match.home_score != null && match.away_score != null

  const rowClass = cn(
    "flex items-center gap-3 border-b px-4 py-3 transition-colors last:border-b-0",
    "hover:bg-muted/50",
    isUserMatch && "bg-[oklch(0.15_0.01_230/0.15)]",
  )

  return (
    <Link href={leagueMatchPath(leagueId, match.id)} className={rowClass}>
      {/* Home team */}
      <div className="flex min-w-0 flex-1 items-center justify-end gap-2.5">
        <div className="min-w-0 text-right">
          <TeamName
            name={match.home_team.name}
            hue={match.home_team.color_hue}
            isUser={isUserMatch && match.home_team.id === userTeamId}
            className={cn(
              "block text-sm",
              isUserMatch && match.home_team.id === userTeamId
                ? "font-bold"
                : "font-semibold",
            )}
          />
          <RaceTag
            race={match.home_team.race}
            hue={match.home_team.color_hue ?? undefined}
            className="mt-0.5 whitespace-nowrap"
          />
        </div>
        <RaceShield
          race={match.home_team.race}
          hue={match.home_team.color_hue ?? undefined}
          label={getTeamInitials(match.home_team.name)}
          size="sm"
          className="flex-shrink-0"
        />
      </div>

      {/* Score */}
      <div className="flex w-16 flex-shrink-0 flex-col items-center justify-center">
        {hasScores ? (
          <>
            <span className="flex items-center gap-1.5">
              <span className="font-display text-xl leading-none font-bold tracking-[0.02em]">
                {match.home_score}
              </span>
              <span
                className="text-xs font-bold"
                style={{ color: "var(--border-heavy)" }}
              >
                –
              </span>
              <span className="font-display text-xl leading-none font-bold tracking-[0.02em]">
                {match.away_score}
              </span>
            </span>
            {isCompleted && (
              <span className="font-display mt-0.5 text-[0.6rem] font-semibold tracking-widest text-[oklch(0.55_0.08_145)]">
                FINAL
              </span>
            )}
          </>
        ) : (
          <span
            className={cn(
              "font-display text-muted-foreground text-lg leading-none font-medium tracking-[0.05em] uppercase",
            )}
          >
            vs
          </span>
        )}
      </div>

      {/* Away team */}
      <div className="flex min-w-0 flex-1 items-center gap-2.5">
        <RaceShield
          race={match.away_team.race}
          hue={match.away_team.color_hue ?? undefined}
          label={getTeamInitials(match.away_team.name)}
          size="sm"
          className="flex-shrink-0"
        />
        <div className="min-w-0">
          <TeamName
            name={match.away_team.name}
            hue={match.away_team.color_hue}
            isUser={isUserMatch && match.away_team.id === userTeamId}
            className={cn(
              "block text-sm",
              isUserMatch && match.away_team.id === userTeamId
                ? "font-bold"
                : "font-semibold",
            )}
          />
          <RaceTag
            race={match.away_team.race}
            hue={match.away_team.color_hue ?? undefined}
            className="mt-0.5 whitespace-nowrap"
          />
        </div>
      </div>
    </Link>
  )
}

function RoundCard({
  round,
  userTeamId,
  leagueId,
  isActive,
}: {
  round: ScheduleRound
  userTeamId: string | null
  leagueId: string
  isActive?: boolean
}) {
  const isCompleted = round.status === "completed"
  const isInProgress = round.status === "in_progress"

  const headingClass = cn(
    "font-display text-2xl leading-none font-bold",
    isInProgress
      ? "text-gold"
      : isCompleted
        ? "text-gold-sm"
        : "text-muted-foreground",
  )

  const goldAnimation =
    isActive !== undefined
      ? isActive
        ? "gold-on 0.4s ease-out forwards"
        : "gold-off 0.45s ease-in forwards"
      : undefined

  return (
    <Card
      className="overflow-visible py-0"
      style={goldAnimation ? { animation: goldAnimation } : undefined}
    >
      <CardContent className="p-0">
        {/* Round header */}
        <div className="flex items-center gap-3 px-5 pt-4 pb-3">
          <h2 className={headingClass}>Round {round.number}</h2>
          <RoundStatusBadge
            status={round.status}
            scheduledAt={round.scheduled_at}
          />
        </div>

        {/* Match list */}
        <div className="border-t">
          {round.matches.length === 0 ? (
            <p className="text-muted-foreground px-5 py-4 text-sm">
              No matches scheduled.
            </p>
          ) : (
            round.matches.map((match) => (
              <MatchRow
                key={match.id}
                match={match}
                userTeamId={userTeamId}
                leagueId={leagueId}
              />
            ))
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function getItemStyle(
  index: number,
  currentIndex: number,
  total: number,
): Record<string, string | number> {
  let offset = index - currentIndex
  if (offset > Math.floor(total / 2)) offset -= total
  if (offset < -Math.floor(total / 2)) offset += total

  const absOffset = Math.abs(offset)
  const translateX = offset * 420
  const translateZ = -absOffset * 300
  const rotateY = offset * -35
  const scale = Math.max(0.5, 1 - absOffset * 0.2)
  const opacity = Math.max(0.1, 1 - absOffset * 0.4)
  const blur = absOffset > 1 ? (absOffset - 1) * 2.5 : 0

  return {
    transform: `translate(-50%, -50%) translateX(${translateX}px) translateZ(${translateZ}px) rotateY(${rotateY}deg) scale(${scale})`,
    opacity,
    filter: blur > 0 ? `blur(${blur}px)` : "none",
    zIndex: 10 - absOffset,
  }
}

export function ScheduleCarousel({
  rounds,
  userTeamId,
  leagueId,
}: {
  rounds: ScheduleRound[]
  userTeamId: string | null
  leagueId: string
}) {
  const initialIndex = useCallback(() => {
    const inProgressIdx = rounds.findIndex((r) => r.status === "in_progress")
    if (inProgressIdx !== -1) return inProgressIdx
    for (let i = rounds.length - 1; i >= 0; i--) {
      if (rounds[i].status === "completed") return i
    }
    return 0
  }, [rounds])

  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [isAnimating, setIsAnimating] = useState(false)
  const [viewportHeight, setViewportHeight] = useState(480)
  const touchStartX = useRef(0)
  const keyLeftRef = useRef<HTMLSpanElement>(null)
  const keyRightRef = useRef<HTMLSpanElement>(null)
  const activeCardRef = useRef<HTMLDivElement | null>(null)

  const goTo = useCallback(
    (index: number) => {
      if (isAnimating || rounds.length === 0) return
      const newIndex = ((index % rounds.length) + rounds.length) % rounds.length
      if (newIndex === currentIndex) return
      setIsAnimating(true)
      setCurrentIndex(newIndex)
      setTimeout(() => setIsAnimating(false), 500)
    },
    [isAnimating, currentIndex, rounds.length],
  )

  const goLeft = useCallback(() => goTo(currentIndex - 1), [goTo, currentIndex])
  const goRight = useCallback(
    () => goTo(currentIndex + 1),
    [goTo, currentIndex],
  )

  const flashKey = useCallback((el: HTMLSpanElement | null) => {
    if (!el) return
    el.style.color = "var(--accent)"
    el.style.borderColor = "var(--accent)"
    el.style.boxShadow = "0 0 8px oklch(0.8 0.16 85 / 0.3)"
    setTimeout(() => {
      el.style.color = ""
      el.style.borderColor = ""
      el.style.boxShadow = ""
    }, 200)
  }, [])

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        flashKey(keyLeftRef.current)
        goLeft()
      }
      if (e.key === "ArrowRight") {
        flashKey(keyRightRef.current)
        goRight()
      }
    }
    document.addEventListener("keydown", handleKey)
    return () => document.removeEventListener("keydown", handleKey)
  }, [goLeft, goRight, flashKey])

  // Size the viewport to the tallest card so rounds with many matches aren't
  // clipped. The active card is rendered at scale=1, rotate=0 so its bounding
  // box is its natural height. ResizeObserver keeps us in sync on re-layouts.
  useEffect(() => {
    const el = activeCardRef.current
    if (!el) return
    const update = () => {
      const h = el.getBoundingClientRect().height
      // +24px buffer for subtle perspective blur on neighboring cards
      if (h > 0) setViewportHeight((prev) => Math.max(prev, Math.ceil(h + 24)))
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [currentIndex, rounds])

  if (rounds.length === 0) return null

  return (
    <>
      {/* 3D Carousel */}
      <div className="flex items-center justify-center gap-4">
        {/* Left nav */}
        <Button
          variant="ghost"
          className="border-border-heavy bg-card text-foreground hover:border-accent hover:text-accent z-20 h-12 w-12 shrink-0 cursor-pointer border-2 transition-all hover:shadow-[0_0_16px_oklch(0.5_0.1_85/0.2)] active:scale-[0.92] active:shadow-[0_0_24px_oklch(0.8_0.16_85/0.3)] disabled:cursor-not-allowed disabled:opacity-40"
          style={{
            boxShadow: "0 2px 8px oklch(0 0 0 / 0.4)",
          }}
          onClick={goLeft}
          disabled={rounds.length <= 1}
          aria-label="Previous round"
        >
          <ChevronLeft className="size-6" />
        </Button>

        {/* Viewport */}
        <div
          className="relative flex-1 overflow-hidden"
          style={{
            height: viewportHeight,
            perspective: 1000,
            perspectiveOrigin: "50% 50%",
            transition: "height 0.3s ease-out",
          }}
          onTouchStart={(e) => {
            touchStartX.current = e.touches[0].clientX
          }}
          onTouchEnd={(e) => {
            const diff = touchStartX.current - e.changedTouches[0].clientX
            if (Math.abs(diff) > 50) {
              if (diff > 0) goRight()
              else goLeft()
            }
          }}
        >
          <div
            className="absolute inset-0"
            style={{ transformStyle: "preserve-3d" }}
          >
            {rounds.map((round, i) => {
              const isActive = i === currentIndex
              const itemStyle = getItemStyle(i, currentIndex, rounds.length)
              return (
                <div
                  key={round.id}
                  ref={isActive ? activeCardRef : undefined}
                  className="absolute top-1/2 left-1/2 w-full max-w-[580px]"
                  style={{
                    transformStyle: "preserve-3d",
                    transition: isActive
                      ? "transform 0.5s cubic-bezier(0.25, 1.15, 0.5, 1), opacity 0.45s cubic-bezier(0.4, 0, 0.2, 1), filter 0.45s cubic-bezier(0.4, 0, 0.2, 1)"
                      : "transform 0.45s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.45s cubic-bezier(0.4, 0, 0.2, 1), filter 0.45s cubic-bezier(0.4, 0, 0.2, 1)",
                    pointerEvents: isActive ? "auto" : "none",
                    ...itemStyle,
                  }}
                >
                  <RoundCard
                    round={round}
                    userTeamId={userTeamId}
                    leagueId={leagueId}
                    isActive={isActive}
                  />
                </div>
              )
            })}
          </div>
        </div>

        {/* Right nav */}
        <Button
          variant="ghost"
          className="border-border-heavy bg-card text-foreground hover:border-accent hover:text-accent z-20 h-12 w-12 shrink-0 cursor-pointer border-2 transition-all hover:shadow-[0_0_16px_oklch(0.5_0.1_85/0.2)] active:scale-[0.92] active:shadow-[0_0_24px_oklch(0.8_0.16_85/0.3)] disabled:cursor-not-allowed disabled:opacity-40"
          style={{
            boxShadow: "0 2px 8px oklch(0 0 0 / 0.4)",
          }}
          onClick={goRight}
          disabled={rounds.length <= 1}
          aria-label="Next round"
        >
          <ChevronRight className="size-6" />
        </Button>
      </div>

      {/* Dot indicators + keyboard hints */}
      <div
        className="mt-4 flex items-center justify-center gap-3"
        aria-label="Round navigation"
      >
        <span
          ref={keyLeftRef}
          className="border-border font-display text-muted-foreground inline-flex items-center justify-center border bg-[oklch(0.17_0.01_50)] px-[5px] text-[0.7rem] font-semibold tracking-[0.04em] uppercase transition-all"
          style={{
            minWidth: 22,
            height: 20,
            boxShadow: "0 1px 0 var(--border-heavy)",
          }}
        >
          ←
        </span>
        {rounds.map((round, i) => {
          const isDotActive = i === currentIndex
          return (
            <Button
              key={round.id}
              variant="ghost"
              onClick={() => goTo(i)}
              aria-label={`Round ${round.number}`}
              className={cn(
                "border-border-heavy hover:border-accent-dim h-2 w-2 cursor-pointer rounded-none border-[1.5px] bg-transparent p-0 transition-all",
                isDotActive &&
                  "border-accent bg-accent shadow-[0_0_6px_oklch(0.8_0.16_85/0.4)]",
              )}
            />
          )
        })}
        <span
          ref={keyRightRef}
          className="border-border font-display text-muted-foreground inline-flex items-center justify-center border bg-[oklch(0.17_0.01_50)] px-[5px] text-[0.7rem] font-semibold tracking-[0.04em] uppercase transition-all"
          style={{
            minWidth: 22,
            height: 20,
            boxShadow: "0 1px 0 var(--border-heavy)",
          }}
        >
          →
        </span>
      </div>
    </>
  )
}
